import React, { useEffect, useState, useRef } from 'react';
import { Card, Table, Button, Modal, Form, Input, InputNumber, message, Popconfirm } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import { getPublicidadAuto, createPublicidadAuto, updatePublicidadAuto, deletePublicidadAuto } from '../../../services/publicidadAuto';
import { useSelector } from 'react-redux';
import { State } from 'sigt';
import { connect } from 'react-redux';

interface PublicidadAutoData {
  id_publicidad: number;
  documento: string;
  mmv: number;
}

const PublicidadAuto: React.FC<any> = ({ thm }) => {
  const [data, setData] = useState<PublicidadAutoData[]>([]);
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const searchInput = useRef<Input>(null);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState<PublicidadAutoData | null>(null);
  const [form] = Form.useForm();


  const token = useSelector((state: State) => state.auth.token);

  const fetchData = async () => {
    if (!token) {
      setData([]);
      return;
    }
    setLoading(true);
    try {
      const res = await getPublicidadAuto(token);
      setData(res.data || []);
    } catch (e) {
      message.error('Error al cargar los registros');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const handleAdd = () => {
    setEditing(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: PublicidadAutoData) => {
    setEditing(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };


  const handleDelete = async (id: number) => {
    if (!token) {
      message.error('No hay token de autenticación.');
      return;
    }
    setLoading(true);
    try {
      await deletePublicidadAuto(id, token);
      message.success('Registro eliminado');
      fetchData();
    } catch (e) {
      message.error('Error al eliminar');
    } finally {
      setLoading(false);
    }
  };


  const handleOk = async () => {
    if (!token) {
      message.error('No hay token de autenticación.');
      return;
    }
    try {
      const values = await form.validateFields();
      setLoading(true);
      const payload = {
        documento: values.documento,
        mmv: values.mmv,
      };
      if (editing) {
        await updatePublicidadAuto(editing.id_publicidad, payload, token);
        message.success('Registro actualizado');
      } else {
        await createPublicidadAuto(payload, token);
        message.success('Registro creado');
      }
      setModalVisible(false);
      fetchData();
    } catch (e) {
      const backendMsg = e?.response?.data?.message;
      message.error(backendMsg || 'Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const getColumnSearchProps = (dataIndex: string) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Buscar`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ width: 188, marginBottom: 8, display: 'block' }}
        />
        <Button
          type="primary"
          onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          icon={<SearchOutlined />}
          size="small"
          style={{ width: 90, marginRight: 8 }}
        >
          Buscar
        </Button>
        <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
          Restablecer
        </Button>
      </div>
    ),
    filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
    onFilter: (value, record) => record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownVisibleChange: visible => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: text =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = clearFilters => {
    clearFilters();
    setSearchText('');
  };

  const columns = [
    {
      title: 'Documento',
      dataIndex: 'documento',
      key: 'documento',
      ...getColumnSearchProps('documento'),
    },
    { title: 'MMV', dataIndex: 'mmv', key: 'mmv' },
    {
      title: <div style={{ textAlign: 'center', minWidth: 120 }}>Acciones</div>,
      key: 'acciones',
      render: (_: any, record: PublicidadAutoData) => (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 16 }}>
          <Button type="link" onClick={() => handleEdit(record)}>Editar</Button>
          <Popconfirm title="¿Seguro de eliminar?" onConfirm={() => handleDelete(record.id_publicidad)} okText="Sí" cancelText="No">
            <Button type="link" danger>Eliminar</Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <>
      <Card
        title="Publicidad"
        headStyle={{ backgroundColor: thm.primaryColor, color: '#fff', padding: '0 20px', fontWeight: 600, fontSize: 18 }}
        bodyStyle={{ overflowY: 'auto' }}
        extra={
          <Button
            style={{ background: '#fff', color: thm.primaryColor, borderColor: thm.primaryColor, fontWeight: 600 }}
            onClick={handleAdd}
          >
            Agregar
          </Button>
        }
      >
        <Table
          rowKey="id_publicidad"
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
        <Modal
          title={editing ? 'Editar Publicidad' : 'Agregar Publicidad'}
          visible={modalVisible}
          onOk={handleOk}
          onCancel={() => setModalVisible(false)}
          okText="Guardar"
          cancelText="Cancelar"
        >
          <Form form={form} layout="vertical">
            <Form.Item name="documento" label="Documento" rules={[{ required: true, message: 'Ingrese el documento' }]}> 
              <Input />
            </Form.Item>
            <Form.Item name="mmv" label="MMV" rules={[{ required: true, message: 'Ingrese el MMV' }]}> 
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Form>
        </Modal>
      </Card>

    </>
  );
};

export default connect((state: State) => ({ thm: state.thm }))(PublicidadAuto);
